import { Link } from 'react-router-dom';
import { Icon } from '../common/Icon';
import { Eyebrow } from '../common/Eyebrow';
import type { Category, Product } from '../../types';
import type { ProductFacts } from '../../lib/productFacts';
import type { FaqEntry } from '../../lib/productFaq';

/**
 * The always-visible, machine-readable face of a product's factual record.
 *
 * The full detail still lives in the accordions below it on the page, but the facts that IDENTIFY a
 * bio-input — what is in it, how it is applied, at what dose, what approval it holds, what pack it
 * comes in — are not identity facts you hide behind an expander, and a crawler summarising the page
 * should not have to guess which collapsed panel held the answer. So they are read straight off the
 * catalogue record and printed once, in one semantic definition list, above the fold.
 *
 * Everything here renders only when the underlying value exists; nothing is defaulted or inferred.
 */

const LANG_NAMES: Record<string, string> = {
  en: 'English',
  hi: 'हिंदी (Hindi)',
  mr: 'मराठी (Marathi)',
  or: 'ଓଡ଼ିଆ (Odia)',
  bn: 'বাংলা (Bengali)',
  gu: 'ગુજરાતી (Gujarati)',
};
export const langLabel = (lang?: string) => (lang ? LANG_NAMES[lang] ?? lang.toUpperCase() : '');

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="border-b border-sand-200 py-3 last:border-0 sm:grid sm:grid-cols-[11rem_minmax(0,1fr)] sm:gap-4">
      <dt className="text-sm font-semibold text-ink-700">{label}</dt>
      <dd className="mt-1 text-[0.95rem] leading-relaxed text-ink-600 sm:mt-0">{children}</dd>
    </div>
  );
}

/**
 * "At a glance" — the answer to "what is this and how do I use it" without opening anything.
 * `cropPageFor` returns a crop-programme path when one exists, so a crop becomes a link into the
 * programme that documents its dose rather than a dead word.
 */
export function ProductGlance({
  product,
  facts,
  category,
  cropPageFor,
}: {
  product: Product;
  facts: ProductFacts;
  category?: Category;
  cropPageFor: (crop: string) => string | null;
}) {
  const crops = product.recommendedCrops ?? [];
  return (
    <dl className="mt-8 rounded-xl border border-sand-200 bg-white p-6 shadow-soft">
      {category && (
        <Row label="Category">
          <Link to={`/product-categories/${category.slug}`} className="font-medium text-brand-700 hover:underline">
            {category.name}
          </Link>
          {category.tagline ? <span className="text-ink-400"> — {category.tagline}</span> : null}
        </Row>
      )}
      {facts.compositionSummary && <Row label="Composition">{facts.compositionSummary}</Row>}
      {facts.applicationMethods.length > 0 && (
        <Row label="How it is applied">{facts.applicationMethods.join(' · ')}</Row>
      )}
      {facts.dosageSummary && <Row label="Dose">{facts.dosageSummary}</Row>}
      {product.variants.length > 0 && (
        <Row label="Pack sizes">{product.variants.map((v) => v.packSize).join(' · ')}</Row>
      )}
      {facts.shelfLife && <Row label="Shelf life">{facts.shelfLife}</Row>}
      {crops.length > 0 && (
        <Row label="Documented crops">
          <span className="flex flex-wrap gap-1.5">
            {crops.map((c) => {
              const to = cropPageFor(c);
              return to ? (
                <Link
                  key={c}
                  to={to}
                  className="inline-flex items-center gap-1 rounded-full border border-brand-200 bg-brand-50/70 px-2.5 py-1 text-sm font-medium text-brand-800 transition-colors hover:border-brand-400"
                >
                  <Icon name="Sprout" className="h-3.5 w-3.5 text-brand-500" />
                  {c}
                </Link>
              ) : (
                <span
                  key={c}
                  className="inline-flex items-center gap-1 rounded-full border border-sand-200 bg-canvas px-2.5 py-1 text-sm text-ink-700"
                >
                  <Icon name="Sprout" className="h-3.5 w-3.5 text-brand-400" />
                  {c}
                </span>
              );
            })}
          </span>
        </Row>
      )}
    </dl>
  );
}

/**
 * Approvals and licence, quoted from the product's own record.
 *
 * Scoped to THIS product on purpose. Approvals differ across the range — NPOP and FCO for the AGN
 * family, EU Reg. 834/2007 and NOP for Biovin, the USDA BioPreferred programme for Hydretain — and
 * "some products are approved" must never be readable as "all products are approved".
 */
export function ApprovalPanel({ product, licence }: { product: Product; licence?: string }) {
  if (!licence) return null;
  return (
    <section className="mt-10 rounded-xl border border-brand-200 bg-brand-50/60 p-6" aria-labelledby="approvals-heading">
      <h2 id="approvals-heading" className="flex items-center gap-2 font-display text-h3 font-semibold text-brand-900">
        <Icon name="ShieldCheck" className="h-5 w-5 text-brand-600" />
        Approvals &amp; licence
      </h2>
      <p className="mt-2 text-brand-900">{licence}</p>
      <p className="mt-3 text-sm text-brand-800/80">
        This applies to {product.name} only. Approvals differ product by product across the GreenbayBio
        range — each product page states its own.
      </p>
    </section>
  );
}

/** Technical documents. `hrefLang` marks the Hindi sheets so a Hindi reader is sent the right file. */
export function DocumentsPanel({
  product,
  compact = false,
}: {
  product: Product;
  compact?: boolean;
}) {
  const resources = product.resources ?? [];
  if (!resources.length) return null;
  return (
    <div className={compact ? 'mt-6 rounded-xl border border-sand-200 bg-white p-5' : 'mt-10'}>
      {compact ? (
        <Eyebrow>Technical documents</Eyebrow>
      ) : (
        <h2 className="font-display text-h3 font-semibold text-ink">Technical documents</h2>
      )}
      <ul className="mt-3 space-y-1">
        {resources.map((r, i) => (
          <li key={`${r.url}-${i}`}>
            <a
              href={r.url}
              target="_blank"
              rel="noopener noreferrer"
              {...(r.lang ? { hrefLang: r.lang } : {})}
              className="group inline-flex items-center gap-2.5 rounded-md py-1.5 text-sm font-medium text-ink-700 transition-colors hover:text-brand-600"
            >
              <Icon name="FileText" className="h-4 w-4 shrink-0 text-brand-500" />
              <span>
                {product.name} {r.label.trim().toLowerCase().startsWith(product.name.toLowerCase()) ? '' : r.label.trim()}
                {r.lang ? <span className="text-ink-500"> · {langLabel(r.lang)}</span> : null}
                <span className="text-ink-400"> (PDF)</span>
              </span>
              <Icon name="Download" className="h-3.5 w-3.5 shrink-0 text-ink-300 opacity-0 transition-opacity group-hover:opacity-100" />
            </a>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-ink-500">
        The data sheet is the primary document. Where it and this page differ, the printed label on your
        pack governs.
      </p>
    </div>
  );
}

/**
 * A visible FAQ. It is deliberately plain text rather than an expander: the answers are short, and a
 * question whose answer is hidden answers nothing for a reader skimming or a crawler extracting.
 */
export function FaqBlock({
  items,
  heading,
  id = 'faq',
}: {
  items: FaqEntry[];
  heading: string;
  id?: string;
}) {
  if (!items.length) return null;
  return (
    <section className="mt-12" aria-labelledby={`${id}-heading`}>
      <h2 id={`${id}-heading`} className="font-display text-h3 font-semibold text-ink">
        {heading}
      </h2>
      <div className="mt-5 divide-y divide-sand-200 border-y border-sand-200">
        {items.map((f) => (
          <div key={f.q} className="py-5">
            <h3 className="font-semibold text-ink">{f.q}</h3>
            <p className="mt-1.5 leading-relaxed text-ink-600">{f.a}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
